// Date + 30-minute slot picker with conflict awareness.
//
// Extracted from BookQuoteAppointmentDialog (iter-73) so New Activity dialogs
// (from Scheduler slot click, Deals, Activities, ClientDetail) all share the
// same look: pick a date, then tap a 30-min slot; existing tasks on the same
// day light up amber so the user avoids double-booking.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";

// 07:00 → 17:30 in 30-min increments (22 slots).
const SLOTS = Array.from({ length: 22 }, (_, i) => {
  const total = 7 * 60 + i * 30;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
});

/** Split an ISO / datetime-local string into `{date: "YYYY-MM-DD", time: "HH:MM"}`. */
function splitISO(iso) {
  if (!iso) return { date: "", time: "" };
  // Accept both "2026-08-10T09:00:00.000Z" and "2026-08-10T09:00" shapes.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    // Fall back to naive substring split so a partially-typed value doesn't blow up.
    return { date: (iso || "").slice(0, 10), time: (iso || "").slice(11, 16) };
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date: `${y}-${m}-${day}`, time: `${hh}:${mm}` };
}

function joinLocalISO(date, time) {
  if (!date) return "";
  const t = time || "09:00";
  return `${date}T${t}:00`;
}

/**
 * @param {object} p
 * @param {string} p.value      ISO / datetime-local string (or "").
 * @param {(next: string) => void} p.onChange  Called with a "YYYY-MM-DDTHH:MM:00" string (matches datetime-local semantics).
 * @param {string=} p.testidPrefix  data-testid prefix (default "activity-when").
 * @param {string=} p.excludeActivityId  Ignore this activity id when computing conflicts (edit mode).
 * @param {boolean=} p.showConflicts     Default true.
 */
export default function DateTimeSlotPicker({
  value, onChange, testidPrefix = "activity-when", excludeActivityId = null, showConflicts = true,
}) {
  const { date, time } = splitISO(value);

  const { data: activitiesForDate = [] } = useQuery({
    queryKey: ["activities-on-date-picker", date],
    enabled: !!date && showConflicts,
    queryFn: async () => (await api.get("/activities")).data,
  });

  const conflictsThatDay = useMemo(() => {
    if (!date) return [];
    return activitiesForDate
      .filter((a) => a.id !== excludeActivityId && (a.due_at || "").startsWith(date) && !a.completed)
      .sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
  }, [activitiesForDate, date, excludeActivityId]);

  const busySlots = useMemo(() => {
    const set = new Set();
    for (const a of conflictsThatDay) {
      if (!a.due_at) continue;
      const t = new Date(a.due_at);
      const hh = String(t.getHours()).padStart(2, "0");
      const mm = String(t.getMinutes()).padStart(2, "0");
      set.add(`${hh}:${mm}`);
    }
    return set;
  }, [conflictsThatDay]);

  const setDate = (d) => onChange(joinLocalISO(d, time));
  const setTime = (t) => onChange(joinLocalISO(date, t));
  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3">
      <div>
        <Label className="stamp-label text-slate-600">Date</Label>
        <Input
          type="date"
          data-testid={`${testidPrefix}-date`}
          value={date}
          min={todayISO}
          onChange={(e) => setDate(e.target.value)}
          className="mt-1 h-10 rounded-sm font-mono"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="stamp-label text-slate-600">Start time</Label>
          {showConflicts && conflictsThatDay.length > 0 && (
            <Badge variant="outline" className="text-[10px] rounded-sm border-amber-300 text-amber-800 bg-amber-50">
              {conflictsThatDay.length} other {conflictsThatDay.length === 1 ? "task" : "tasks"} that day
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
          {SLOTS.map((s) => {
            const isBusy = busySlots.has(s);
            const isSelected = time === s;
            // Iter-73: compose Selected + Busy so a pre-filled slot that also
            // has an existing task shows a cyan fill with an amber outline
            // ring — the user sees both the selection and the conflict.
            let cls = "bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:bg-cyan-50";
            if (isSelected && isBusy) {
              cls = "bg-cyan-600 text-white border-amber-400 ring-2 ring-amber-300";
            } else if (isSelected) {
              cls = "bg-cyan-600 text-white border-cyan-600";
            } else if (isBusy) {
              cls = "bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400";
            }
            return (
              <button
                key={s}
                type="button"
                data-testid={`${testidPrefix}-slot-${s}`}
                onClick={() => setTime(s)}
                className={`h-8 rounded-sm text-[11px] font-mono font-semibold border transition-colors ${cls}`}
              >
                {s}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-cyan-600 rounded-sm" /> Selected</span>
          {showConflicts && (
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-50 border border-amber-200 rounded-sm" /> Existing task</span>
          )}
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-200 rounded-sm" /> Free</span>
        </div>
      </div>
      {showConflicts && conflictsThatDay.length > 0 && (
        <div className="border border-amber-200 bg-amber-50/60 rounded-sm p-3">
          <div className="flex items-center gap-1.5 stamp-label text-amber-800 mb-1.5">
            <AlertCircle size={12} /> Already scheduled that day
          </div>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {conflictsThatDay.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-[11px] text-slate-700">
                <span className="truncate mr-2">{a.title}</span>
                <span className="font-mono text-amber-700 shrink-0">
                  {new Date(a.due_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
            {conflictsThatDay.length > 5 && (
              <div className="text-[10px] text-slate-500">+ {conflictsThatDay.length - 5} more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
